import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Search, Home, Calculator, LineChart, BookOpen, MapPin } from "lucide-react";

const USEFUL_LINKS = [
  { to: "/annonces", label: "Voir les annonces", icon: MapPin },
  { to: "/simulateur", label: "Simulateur", icon: Calculator },
  { to: "/estimer", label: "Estimer mon bien", icon: LineChart },
  { to: "/blog", label: "Guides & fiscalité", icon: BookOpen },
  { to: "/comment-ca-marche", label: "Comment ça marche", icon: Home },
];

export default function NotFound() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    const term = q.trim().toLowerCase();
    if (!term) return navigate("/annonces");
    if (["blog", "guide", "guides"].some((k) => term.includes(k))) return navigate("/blog");
    if (term.includes("simul")) return navigate("/simulateur");
    if (term.includes("estim")) return navigate("/estimer");
    if (term.includes("agence")) return navigate("/agences");
    if (term.includes("contact")) return navigate("/contact");
    if (term.includes("dépos") || term.includes("depos")) return navigate("/deposer-annonce");
    return navigate(`/annonces?q=${encodeURIComponent(term)}`);
  };

  return (
    <>
      <SEO title="Page introuvable — venteaterme.fr" description="Cette page n’existe pas ou plus. Retrouvez nos annonces, notre simulateur et nos guides sur la vente à terme immobilière." />
      <section className="min-h-[70vh] pt-24 pb-24 bg-navy text-cream flex items-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center w-full">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-6">Erreur 404</div>
          <div className="font-serif text-7xl md:text-9xl leading-none mb-6 text-accent-brand tabular-nums">404</div>
          <h1 className="font-serif text-3xl md:text-5xl leading-tight mb-4 text-balance">Cette page n’existe pas ou a été déplacée.</h1>
          <p className="text-cream/75 max-w-xl mx-auto mb-10 leading-relaxed">
            Pas d’inquiétude : lancez une recherche ou rejoignez une section utile ci-dessous.
          </p>

          <form onSubmit={submit} className="max-w-xl mx-auto mb-12" data-testid="404-search-form">
            <div className="flex items-stretch bg-white text-navy">
              <div className="flex items-center pl-4 pr-2">
                <Search size={18} className="text-navy/50" />
              </div>
              <Input
                data-testid="404-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Que cherchez-vous ? (annonces, simulateur, guides…)"
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-12 flex-1 text-navy"
              />
              <button type="submit" data-testid="404-search-submit"
                className="bg-accent-brand text-white px-5 font-bold tracking-wide hover:bg-[#B08D22] transition-colors">
                Rechercher
              </button>
            </div>
          </form>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {USEFUL_LINKS.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`404-link-${to.replace(/\//g, "") || "home"}`}
                className="flex items-center gap-3 border border-cream/25 px-4 py-3 text-sm text-cream hover:bg-cream hover:text-navy transition-colors"
              >
                <Icon size={16} className="text-accent-brand shrink-0" />
                <span className="font-medium">{label}</span>
              </Link>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/" data-testid="404-home-link" className="inline-block text-sm text-cream/70 hover:text-accent-brand underline underline-offset-4">
              ← Retour à l’accueil
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
